<template>
  <div
    id="daterangecalendar"
    ref="DatepickerRangeRef"
    class="calendar-picker"
    :class="[{ 'calendar-picker--overlay': overlay }, { open: isShow && overlay }]"
    v-daterangecalendar:away="trigger"
  >
    <slot
      :value="pickerValue"
      :placeholder="givenPlaceholder"
      :clear="clearPicker"
    >
      <label class="calendar-picker__label">
        <input
          ref="DatepickerRangeInputRef"
          type="text"
          class="calendar-picker__input"
          v-bind="$attrs"
          v-model="beatifyPickerValue"
          :placeholder="givenPlaceholder"
          @keyup="keyUp"
        />
        <i
          class="calendar-picker__icon icon-calendar-month"
          @click="pickerValue ? clearPicker() : $refs.DatepickerRangeInputRef.focus()"
        ></i>
      </label>
    </slot>
    <div
      v-show="isShow"
      ref="PopupCalendarRef"
      class="popup-calendar"
      :class="placement ? 'popup-calendar--placement-left' : 'popup-calendar--placement-right'"
    >
      <div class="popup-calendar__wrapper">
        <div
          class="popup-calendar__box"
          :class="placement ? 'popup-calendar__box--place-left' : 'popup-calendar__box--place-right'"
        >
          <div class="popup-calendar__main">

            <!--          Calendar-->
            <div class="popup-calendar__calendar">

              <div
                class="popup-calendar__calendar__left-wrap"
                :class="{
      'popup-calendar__calendar__left-wrap--range': asRange() && !asSingle,
    }"
              >
                <DatepickerRangeHeader
                  :panel="panel.previous"
                  :calendar="calendar.previous"
                />
                <div class="popup-calendar__calendar__header">
                  <div v-show="panel.previous.calendar">
                    <DatepickerRangeWeek :weeks="weeks" />
                    <DatepickerRangeCalendar
                      :calendar="calendar.previous"
                      :weeks="weeks"
                      :as-range="asRange()"
                      @update:date="setDate"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="asRange() && !asSingle"
                class="popup-calendar__calendar__right-wrap"
              >
                <DatepickerRangeHeader
                  as-prev-or-next
                  :panel="panel.next"
                  :calendar="calendar.next"
                />
                <div class="popup-calendar__calendar__header">
                  <div v-show="panel.next.calendar">
                    <DatepickerRangeWeek :weeks="weeks" />
                    <DatepickerRangeCalendar
                      as-prev-or-next
                      :calendar="calendar.next"
                      :weeks="weeks"
                      :as-range="asRange()"
                      @update:date="setDate"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, provide, nextTick, isProxy, watchEffect, watch, unref, onMounted } from "vue";
import { useCurrentDate, useDisableDate, useBetweenRange, useNextDate, usePreviousDate, useToValueFromArray, useToValueFromString, useDirective, useVisibleViewport } from "@/components/UI/Calendar/lib/fn";

const dayjs = useDayjs();

import DatepickerRangeHeader from "@/components/UI/Calendar/components/HeaderCalendar.vue";
import DatepickerRangeWeek from "@/components/UI/Calendar/components/Week.vue";
import DatepickerRangeCalendar from "@/components/UI/Calendar/components/Calendar.vue";

export default defineComponent({
  components: {
    DatepickerRangeHeader,
    DatepickerRangeWeek,
    DatepickerRangeCalendar,
  },
  directives: {
    daterangecalendar: {
      mounted: (el, binding) => {
        useDirective(binding);
      },
      updated: (el, binding) => {
        useDirective(binding);
      },
    },
  },
  props: {
    overlay: Boolean,
    asSingle: Boolean,
    useRange: Boolean,
    placeholder: {
      type: [Boolean, String],
      default: false,
    },
    i18n: {
      type: String,
      default: "en",
    },
    disableDate: {
      type: [Boolean, Array, Function],
      default: false,
    },
    trigger: {
      type: String,
      default: null,
    },
    autoApply: {
      type: Boolean,
      default: true,
    },
    separator: {
      type: String,
      default: " - ",
    },
    formatter: {
      type: Object,
      default: () => ({
        date: 'YYYY-MM-DDTHH:mm:ssZ[Z]',
        beatify: 'DD/MM/YYYY',
        month: "MMM",
      }),
    },
    modelValue: {
      type: [Array, Object, String],
      default: [],
    },
    startFrom: {
      type: [Object, String],
      default: () => new Date(),
    },
  },
  inheritAttrs: false,
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const PopupCalendarRef = ref(null);
    const DatepickerRangeRef = ref(null);
    const DatepickerRangeInputRef = ref(null);
    const isShow = ref(false);
    const placement = ref(true);
    const givenPlaceholder = ref("");
    const selection = ref(null);
    const pickerValue = ref("");
    const hoverValue = ref([]);
    const applyValue = ref([]);
    const previous = ref(null);
    const next = ref(null);
    const panel = reactive({
      previous: {
        calendar: true,
        month: false,
        year: false,
      },
      next: {
        calendar: true,
        month: false,
        year: false,
      },
    });

    dayjs.locale(props.i18n);

    const datepicker = ref({
      previous: dayjs(),
      next: dayjs().add(1, "month"),
      year: {
        previous: dayjs().year(),
        next: dayjs().year(),
      },
      weeks: dayjs.weekdaysShort(),
      months: props.formatter.month === "MMM" ? dayjs.monthsShort() : dayjs.months(),
    });
    const weeks = computed(() => datepicker.value.weeks);
    const months = computed(() => datepicker.value.months);
    const calendar = computed(() => {
      const { previous, next, year } = unref(datepicker);
      return {
        previous: {
          date: () => {
            return usePreviousDate(previous)
              .concat(useCurrentDate(previous))
              .concat(useNextDate(previous))
              .map((v) => {
                v.today = v.isToday();
                v.active = previous.month() === v.month();
                v.off = previous.month() !== v.month();
                v.sunday = v.day() === 0;
                v.disabled = useDisableDate(v, props) && !inRangeDate(v);
                v.inRange = () => {
                  if (props.asSingle && !props.useRange) {
                    return previous.month() !== v.month();
                  }
                };
                v.hovered = () => {
                  if (!asRange()) return false;
                  if (hoverValue.value.length > 1) {
                    return (v.isBetween(hoverValue.value[0], hoverValue.value[1], "date", "()") || v.isBetween(hoverValue.value[1], hoverValue.value[0], "date", "()")) && previous.month() === v.month();
                  }
                  return false;
                };
                v.duration = () => {
                  return false;
                };
                return v;
              });
          },
          month: previous && previous.format(props.formatter.month),
          year: previous && previous.year(),
          years: () => {
            return Array.from(
              {
                length: 12,
              },
              (v, k) => year.previous + k
            );
          },
          onPrevious: () => {
            datepicker.value.previous = previous.subtract(1, "month");
          },
          onNext: () => {
            datepicker.value.previous = previous.add(1, "month");
            if (previous.diff(next, "month") === -1) {
              datepicker.value.next = next.add(1, "month");
            }
          },
          onPreviousYear: () => {
            datepicker.value.year.previous = datepicker.value.year.previous - 12;
          },
          onNextYear: () => {
            datepicker.value.year.previous = datepicker.value.year.previous + 12;
          },
          openMonth: () => {
            panel.previous.month = !panel.previous.month;
            panel.previous.year = false;
            panel.previous.calendar = !panel.previous.month;
          },
          setMount: ($event) => {
            datepicker.value.previous = previous.month($event);
            panel.previous.month = !panel.previous.month;
            panel.previous.year = false;
            panel.previous.calendar = !panel.previous.month;
            nextTick(() => {
              if (datepicker.value.next.isSame(datepicker.value.previous, "month") || datepicker.value.next.isBefore(datepicker.value.previous)) {
                datepicker.value.next = datepicker.value.previous.add(1, "month");
              }
              datepicker.value.year.next = datepicker.value.next.year();
            });
          },
          openYear: () => {
            panel.previous.year = !panel.previous.year;
            panel.previous.month = false;
            panel.previous.calendar = !panel.previous.year;
          },
          setYear: ($event, asNext) => {
            if (!asNext) {
              datepicker.value.previous = previous.year($event);
              panel.previous.year = !panel.previous.year;
              panel.previous.calendar = !panel.previous.year;
              nextTick(() => {
                if (datepicker.value.next.isSame(datepicker.value.previous, "month") || datepicker.value.next.isBefore(datepicker.value.previous)) {
                  datepicker.value.next = datepicker.value.previous.add(1, "month");
                }
                datepicker.value.year.previous = datepicker.value.previous.year();
                datepicker.value.year.next = datepicker.value.next.year();
              });
            }
          },
        },
        next: {
          date: () => {
            return usePreviousDate(next)
              .concat(useCurrentDate(next))
              .concat(useNextDate(next))
              .map((v) => {
                v.today = v.isToday();
                v.active = next.month() === v.month();
                v.off = next.month() !== v.month();
                v.sunday = v.day() === 0;
                v.disabled = useDisableDate(v, props) && !inRangeDate(v);
                v.inRange = () => {
                  if (props.asSingle && !props.useRange) {
                    return next.month() !== v.month();
                  }
                };
                v.hovered = () => {
                  if (hoverValue.value.length > 1) {
                    return (v.isBetween(hoverValue.value[0], hoverValue.value[1], "date", "()") || v.isBetween(hoverValue.value[1], hoverValue.value[0], "date", "()")) && next.month() === v.month();
                  }
                  return false;
                };
                v.duration = () => {
                  return false;
                };
                return v;
              });
          },
          month: next && next.format(props.formatter.month),
          year: next && next.year(),
          years: () => {
            return Array.from(
              {
                length: 12,
              },
              (v, k) => year.next + k
            );
          },
          onPrevious: () => {
            datepicker.value.next = next.subtract(1, "month");
            if (next.diff(previous, "month") === 1) {
              datepicker.value.previous = previous.subtract(1, "month");
            }
          },
          onNext: () => {
            datepicker.value.next = next.add(1, "month");
          },
          onPreviousYear: () => {
            datepicker.value.year.next = datepicker.value.year.next - 12;
          },
          onNextYear: () => {
            datepicker.value.year.next = datepicker.value.year.next + 12;
          },
          openMonth: () => {
            panel.next.month = !panel.next.month;
            panel.next.year = false;
            panel.next.calendar = !panel.next.month;
          },
          setMount: ($event) => {
            datepicker.value.next = next.month($event);
            panel.next.month = !panel.next.month;
            panel.next.year = false;
            panel.next.calendar = !panel.next.month;
            nextTick(() => {
              if (datepicker.value.previous.isSame(datepicker.value.next, "month") || datepicker.value.previous.isAfter(datepicker.value.next)) {
                datepicker.value.previous = datepicker.value.next.subtract(1, "month");
              }
              datepicker.value.year.previous = datepicker.value.previous.year();
            });
          },
          openYear: () => {
            panel.next.year = !panel.next.year;
            panel.next.month = false;
            panel.next.calendar = !panel.next.year;
          },
          setYear: ($event, asNext) => {
            if (asNext) {
              datepicker.value.next = next.year($event);
              panel.next.year = !panel.next.year;
              panel.next.month = false;
              panel.next.calendar = !panel.next.year;
              nextTick(() => {
                if (datepicker.value.previous.isSame(datepicker.value.next, "month") || datepicker.value.previous.isAfter(datepicker.value.next)) {
                  datepicker.value.previous = datepicker.value.next.subtract(1, "month");
                }
                datepicker.value.year.previous = datepicker.value.previous.year();
                datepicker.value.year.next = datepicker.value.next.year();
              });
            }
          },
        },
      };
    });

    const useArray = () => Array.isArray(props.modelValue);

    const useObject = () => typeof props.modelValue === "object";

    const asRange = () => {
      if (!props.useRange && !props.asSingle) {
        return true;
      } else if (!props.useRange && props.asSingle) {
        return false;
      } else if (props.useRange && !props.asSingle) {
        return true;
      } else return !!(props.useRange && props.asSingle);
    };

    const inRangeDate = (date) => {
      if (pickerValue.value === "") return false;
      let s, e;
      if (useArray()) {
        const [start, end] = props.modelValue;
        s = start;
        e = end;
      } else if (useObject()) {
        if (props.modelValue) {
          const [start, end] = Object.values(props.modelValue);
          s = start;
          e = end;
        }
      } else {
        const [start, end] = props.modelValue.split(props.separator);
        s = start;
        e = end;
      }

      return date.isBetween(dayjs(s, props.formatter.date, true), dayjs(e, props.formatter.date, true), "date", "[]");
    };

    const show = () => {
      isShow.value = true;
    };

    const hide = () => {
      isShow.value = false;
    };

    const force = () => {
      previous.value = null;
      next.value = null;
      hoverValue.value = [];
      selection.value = null;
    };

    const clearPicker = () => {
      pickerValue.value = "";
      if (useArray()) {
        emit("update:modelValue", []);
      } else if (useObject()) {
        const obj = {};
        const [start, end] = Object.keys(props.modelValue);
        obj[start] = "";
        obj[end] = "";
        emit("update:modelValue", obj);
      } else {
        emit("update:modelValue", "");
      }
      applyValue.value = [];
      DatepickerRangeInputRef.value && DatepickerRangeInputRef.value.focus();
    };

    const keyUp = () => {
      if (asRange()) {
        const [s, e] = pickerValue.value.split(props.separator);
        const [sd, ed] = [dayjs(s, props.formatter.date, true), dayjs(e, props.formatter.date, true)];
        if (sd.isValid() && ed.isValid()) {
          setDate(sd);
          setDate(ed);
          if (useArray()) {
            emit("update:modelValue", [s, e]);
          } else if (useObject()) {
            const obj = {};
            const [start, end] = Object.keys(props.modelValue);
            obj[start] = s;
            obj[end] = e;
            emit("update:modelValue", obj);
          } else {
            emit(
              "update:modelValue",
              useToValueFromArray(
                {
                  previous: sd,
                  next: ed,
                },
                props
              )
            );
          }
        }
      } else {
        const d = dayjs(pickerValue.value, props.formatter.date, true);
        if (d.isValid()) {
          setDate(d);
          if (useArray()) {
            emit("update:modelValue", [pickerValue.value]);
          } else if (useObject()) {
            const obj = {};
            const [start] = Object.keys(props.modelValue);
            obj[start] = pickerValue.value;
            emit("update:modelValue", obj);
          } else {
            emit("update:modelValue", pickerValue.value);
          }
        }
      }
    };

    const setDate = (date, asNext) => {
      if (asRange()) {
        if (previous.value) {
          next.value = date;
          if (props.autoApply) {
            if (date.isBefore(previous.value)) {
              pickerValue.value = useToValueFromArray(
                {
                  previous: date,
                  next: previous.value,
                },
                props
              );
            } else {
              pickerValue.value = useToValueFromArray(
                {
                  previous: previous.value,
                  next: date,
                },
                props
              );
            }
            const [s, e] = pickerValue.value.split(props.separator);

            if (useArray()) {
              emit("update:modelValue", [dayjs(s, props.formatter.date, true).format(props.formatter.date), dayjs(e, props.formatter.date, true).format(props.formatter.date)]);
            } else if (useObject()) {
              const obj = {};
              const [start, end] = Object.keys(props.modelValue);
              obj[start] = s;
              obj[end] = e;
              emit("update:modelValue", obj);
            } else {
              emit(
                "update:modelValue",
                useToValueFromArray(
                  {
                    previous: dayjs(s, props.formatter.date, true),
                    next: dayjs(e, props.formatter.date, true),
                  },
                  props
                )
              );
            }
            isShow.value = false;
            applyValue.value = [];
            if (!dayjs(s, props.formatter.date, true).isSame(dayjs(e, props.formatter.date, true), "month")) {
              datepicker.value.previous = dayjs(s, props.formatter.date, true);
              datepicker.value.next = dayjs(e, props.formatter.date, true);
            }
            force();
          } else {
            if (previous.value.isAfter(date, "month")) {
              applyValue.value = [date, previous.value];
            } else {
              applyValue.value = [previous.value, date];
            }
            const [s, e] = applyValue.value;

            if (!s.isSame(e, "month")) {
              datepicker.value.previous = s;
              datepicker.value.next = e;
            }
            force();
          }
        } else {
          applyValue.value = [];
          previous.value = date;
          selection.value = date;
          hoverValue.value.push(date);
          applyValue.value.push(date);

          if (asNext) {
            datepicker.value.next = date;
            if (datepicker.value.previous.isSame(date, "month")) {
              datepicker.value.next = date.add(1, "month");
            }
          } else {
            datepicker.value.previous = date;
            if (datepicker.value.next.isSame(date, "month")) {
              datepicker.value.previous = datepicker.value.next;
              datepicker.value.next = date.add(1, "month");
            }
          }
        }
      } else {
        if (props.autoApply) {
          pickerValue.value = useToValueFromString(date, props);
          if (useArray()) {
            emit("update:modelValue", [pickerValue.value]);
          } else if (useObject()) {
            const obj = {};
            const [start] = Object.keys(props.modelValue);
            obj[start] = pickerValue.value;
            emit("update:modelValue", obj);
          } else {
            emit("update:modelValue", pickerValue.value);
          }
          isShow.value = false;
          applyValue.value = [];
          force();
        } else {
          applyValue.value = [date];
          force();
        }
      }
    };

    const applyDate = () => {
      if (applyValue.value.length < 1) return false;
      let date;
      if (asRange()) {
        const [s, e] = applyValue.value;
        if (e.isBefore(s)) {
          date = useToValueFromArray(
            {
              previous: e,
              next: s,
            },
            props
          );
        } else {
          date = useToValueFromArray(
            {
              previous: s,
              next: e,
            },
            props
          );
        }
      } else {
        const [s] = applyValue.value;
        date = s;
      }
      if (asRange()) {
        const [s, e] = date.split(props.separator);

        if (useArray()) {
          emit("update:modelValue", [dayjs(s, props.formatter.date, true).format(props.formatter.date), dayjs(e, props.formatter.date, true).format(props.formatter.date)]);
        } else if (useObject()) {
          const obj = {};
          const [start, end] = Object.keys(props.modelValue);
          obj[start] = s;
          obj[end] = e;
          emit("update:modelValue", obj);
        } else {
          emit(
            "update:modelValue",
            useToValueFromArray(
              {
                previous: dayjs(s, props.formatter.date, true),
                next: dayjs(e, props.formatter.date, true),
              },
              props
            )
          );
        }
        pickerValue.value = date;
      } else {
        pickerValue.value = date.format(props.formatter.date);
        if (useArray()) {
          emit("update:modelValue", [pickerValue.value]);
        } else if (useObject()) {
          const obj = {};
          const [start] = Object.keys(props.modelValue);
          obj[start] = pickerValue.value;
          emit("update:modelValue", obj);
        } else {
          emit("update:modelValue", pickerValue.value);
        }
      }
    };

    const atMouseOver = (date) => {
      if (!asRange()) return false;
      if (previous.value) {
        hoverValue.value = [previous.value, date];
      } else {
        hoverValue.value = [];
        return false;
      }
    };

    const isBetweenRange = (date) => {
      if (previous.value && props.autoApply) return false;
      let s, e;
      if (hoverValue.value.length > 1) {
        const [start, end] = hoverValue.value;
        s = dayjs(start, props.formatter.date, true);
        e = dayjs(end, props.formatter.date, true);
      } else {
        if (useArray()) {
          if (props.autoApply) {
            const [start, end] = props.modelValue;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          } else {
            const [start, end] = applyValue.value;
            s = dayjs(start, props.formatter.date, true);
            e = dayjs(end, props.formatter.date, true);
          }
        } else if (useObject()) {
          if (props.autoApply) {
            if (props.modelValue) {
              const [start, end] = Object.values(props.modelValue);
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            }
          } else {
            const [start, end] = applyValue.value;
            s = dayjs(start, props.formatter.date, true);
            e = dayjs(end, props.formatter.date, true);
          }
        } else {
          if (props.autoApply) {
            const [start, end] = props.modelValue ? props.modelValue.split(props.separator) : [false, false];
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          } else {
            const [start, end] = applyValue.value;
            s = dayjs(start, props.formatter.date, true);
            e = dayjs(end, props.formatter.date, true);
          }
        }
      }
      if (s && e) {
        return useBetweenRange(date, {
          previous: s,
          next: e,
        });
      }
      return false;
    };

    const datepickerClasses = (date) => {
      const { today, active, off, disabled } = date;
      let classes, s, e;
      if (asRange()) {
        if (useArray()) {
          if (selection.value) {
            const [start, end] = hoverValue.value;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          } else {
            if (props.autoApply) {
              const [start, end] = props.modelValue;
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            } else {
              const [start, end] = applyValue.value;
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            }
          }
        } else if (useObject()) {
          if (selection.value) {
            const [start, end] = hoverValue.value;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          } else {
            if (props.autoApply) {
              const [start, end] = props.modelValue ? Object.values(props.modelValue) : [false, false];
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            } else {
              const [start, end] = applyValue.value;
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            }
          }
        } else {
          if (selection.value) {
            const [start, end] = hoverValue.value;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          } else {
            if (props.autoApply) {
              const [start, end] = props.modelValue ? props.modelValue.split(props.separator) : [false, false];
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            } else {
              const [start, end] = applyValue.value;
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            }
          }
        }
      } else {
        if (useArray()) {
          if (props.autoApply) {
            if (props.modelValue.length > 0) {
              const [start] = props.modelValue;
              s = dayjs(start, props.formatter.date, true);
            }
          } else {
            const [start] = applyValue.value;
            s = start && dayjs(start, props.formatter.date, true);
          }
        } else if (useObject()) {
          if (props.autoApply) {
            if (props.modelValue) {
              const [start] = Object.values(props.modelValue);
              s = dayjs(start, props.formatter.date, true);
            }
          } else {
            const [start] = applyValue.value;
            s = start && dayjs(start, props.formatter.date, true);
          }
        } else {
          if (props.autoApply) {
            if (props.modelValue) {
              const [start] = props.modelValue.split(props.separator);
              s = dayjs(start, props.formatter.date, true);
            }
          } else {
            const [start] = applyValue.value;
            s = start && dayjs(start, props.formatter.date, true);
          }
        }
      }
      if (active) {
        classes = today
          ? ` calendar-days__day-btn--today`
          : disabled
            ? ` calendar-days__day-btn--disabled`
            : date.isBetween(s, e, "date", "()") && typeof e !== "undefined"
              ? ` calendar-days__day-btn--between-range`
              : ` calendar-days__day-btn--not-between-range`;
      }
      if (off) {
        classes = ` calendar-days__day-btn--off`;
      }

      if (s && e && !off && !disabled) {
        if (date.isSame(s, "date")) {
          classes = e.isAfter(s, "date") ? " calendar-days__day-btn--end-after-start" : " calendar-days__day-btn--start-after-end";
          if (s.isSame(e, "date")) {
            classes = ` calendar-days__day-btn--start-same-as-end`;
          }
        }
        if (date.isSame(e, "date")) {
          classes = e.isAfter(s, "date") ? " calendar-days__day-btn--start-after-end" : " calendar-days__day-btn--end-after-start";
          if (s.isSame(e, "date")) {
            classes = ` calendar-days__day-btn--start-same-as-end`;
          }
        }
      } else if (s) {
        if (date.isSame(s, "date") && !off) {
          classes = ` calendar-days__day-btn--start-same-as-end`;
        }
      }

      return classes;
    };

    const betweenRangeClasses = (date) => {
      let classes, s, e;
      classes = "";
      if (!asRange()) return classes;
      if (useArray()) {
        if (hoverValue.value.length > 1) {
          const [start, end] = hoverValue.value;
          s = start && dayjs(start, props.formatter.date, true);
          e = end && dayjs(end, props.formatter.date, true);
        } else {
          if (props.autoApply) {
            const [start, end] = props.modelValue;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          } else {
            const [start, end] = applyValue.value;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          }
        }
      } else if (useObject()) {
        if (hoverValue.value.length > 1) {
          const [start, end] = hoverValue.value;
          s = start && dayjs(start, props.formatter.date, true);
          e = end && dayjs(end, props.formatter.date, true);
        } else {
          if (props.autoApply) {
            if (props.modelValue) {
              const [start, end] = Object.values(props.modelValue);
              s = start && dayjs(start, props.formatter.date, true);
              e = end && dayjs(end, props.formatter.date, true);
            }
          } else {
            const [start, end] = applyValue.value;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          }
        }
      } else {
        if (hoverValue.value.length > 1) {
          const [start, end] = hoverValue.value;
          s = start && dayjs(start, props.formatter.date, true);
          e = end && dayjs(end, props.formatter.date, true);
        } else {
          if (props.autoApply) {
            const [start, end] = props.modelValue ? props.modelValue.split(props.separator) : [false, false];
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          } else {
            const [start, end] = applyValue.value;
            s = start && dayjs(start, props.formatter.date, true);
            e = end && dayjs(end, props.formatter.date, true);
          }
        }
      }

      if (s && e) {
        if (date.isSame(s, "date")) {
          if (e.isBefore(s)) {
            classes += ` calendar-days__range-line--rounded-right calendar-days__range-line--inset-zero`;
          }
          if (s.isBefore(e)) {
            classes += ` calendar-days__range-line--rounded-left calendar-days__range-line--inset-zero`;
          }
        } else if (date.isSame(e, "date")) {
          if (e.isBefore(s)) {
            classes += ` calendar-days__range-line--rounded-left calendar-days__range-line--inset-zero`;
          }
          if (s.isBefore(e)) {
            classes += ` calendar-days__range-line--rounded-right calendar-days__range-line--inset-zero`;
          }
        } else {
          classes += ` calendar-days__range-line--inset-zero`;
        }
      }
      return classes;
    };

    watch(
      () => isShow.value,
      () => {
        nextTick(() => {
          placement.value = useVisibleViewport(PopupCalendarRef.value);
          force();
        });
      }
    );

    watch(
      () => applyValue.value,
      (newValue) => {
        if (newValue.length > 0) {
          panel.previous.calendar = true;
          panel.previous.month = false;
          panel.previous.year = false;

          panel.next.calendar = true;
          panel.next.month = false;
          panel.next.year = false;
        }
      }
    );

    const forceEmit = (s, e) => {
      datepicker.value.previous = dayjs(s, props.formatter.date, true);
      datepicker.value.next = dayjs(e, props.formatter.date, true);
      if (
        dayjs.duration(datepicker.value.next.diff(datepicker.value.previous)).$d
          .months === 2 ||
        (dayjs.duration(datepicker.value.next.diff(datepicker.value.previous))
          .$d.months === 1 &&
          dayjs.duration(datepicker.value.next.diff(datepicker.value.previous))
            .$d.days === 7)
      ) {
        datepicker.value.next = datepicker.value.next.subtract(1, 'month');
      }
      if (
        datepicker.value.next.isSame(datepicker.value.previous, 'month') ||
        datepicker.value.next.isBefore(datepicker.value.previous)
      ) {
        datepicker.value.next = datepicker.value.previous.add(1, 'month');
      }
    };

    watch(
      () => props.modelValue,
      (newValue) => {
        pickerValue.value = newValue[0] === newValue[1] ? newValue[1] : `${newValue[0]}${props.separator}${newValue[1]}`;
        forceEmit(newValue[0], newValue[1]);
      }
    );

    watchEffect(() => {
      if (!props.placeholder) {
        if (asRange()) {
          givenPlaceholder.value = `${props.formatter.beatify}${props.separator}${props.formatter.beatify}`;
        } else {
          givenPlaceholder.value = props.formatter.beatify;
        }
      } else {
        givenPlaceholder.value = props.placeholder;
      }
    });

    const beatifyPickerValue = computed(() => {
      if (pickerValue.value.length > 0) {
        if (asRange()) {
          const [s, e] = pickerValue.value.split(props.separator);

          return typeof e === 'undefined'
            ? dayjs(s, props.formatter.date, true).format(props.formatter.beatify)
            : `${dayjs(s, props.formatter.date, true).format(props.formatter.beatify)}${props.separator}${dayjs(e, props.formatter.date, true).format(props.formatter.beatify)}`;
        } else {
          return dayjs(pickerValue.value, props.formatter.date, true).format(props.formatter.beatify);
        }
      }
      return "";
    })

    provide("isBetweenRange", isBetweenRange);
    provide("betweenRangeClasses", betweenRangeClasses);
    provide("datepickerClasses", datepickerClasses);
    provide("atMouseOver", atMouseOver);

    return {
      PopupCalendarRef,
      DatepickerRangeRef,
      DatepickerRangeInputRef,
      isShow,
      placement,
      givenPlaceholder,
      previous,
      next,
      panel,
      pickerValue,
      hoverValue,
      applyValue,
      datepicker,
      calendar,
      weeks,
      months,
      asRange,
      show,
      hide,
      keyUp,
      setDate,
      applyDate,
      clearPicker,
      beatifyPickerValue
    };
  },
});
</script>

<style
  scoped
  lang="scss"
>
.calendar-picker {
  position: relative;

  &--overlay {
    &::before {
      content: "";
      display: none;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: #000000;
      transition-property: opacity;
      //transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      //transition-duration: 300ms;
      transition-duration: 200ms;
      transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
      opacity: 0;
    }

    &.open::before {
      display: block;
      opacity: 0.5;
    }
  }

  &__label {
    display: block;
    position: relative;
  }

  &__input {
    height: 40px;
    border: 0.1rem solid #344859;
    background-color: rgba(10, 22, 29, 0.62);
    padding: 0 3rem 0 1rem;
    cursor: pointer;
    outline: none;
    color: #ffffff;
    letter-spacing: 0.04em;
    border-radius: 3.6rem;

    display: block;
    overflow: hidden;
    position: relative;
    width: 100%;
    font-size: 15px;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 300ms;

      //&::placeholder {
      //    color: red;
      //}
  }

  &__span {
    display: inline-flex;
    overflow: hidden;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    align-items: center;
    border-radius: 0.375rem;
  }

  &__btn {
    padding: 0.25rem 0.5rem;
    margin-right: 0.25rem;
    border-radius: 0.375rem;
  }

  &__svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.8rem;
    height: 1.8rem;
    font-size: 1.7rem;
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    transition: color .1s linear;
  }
}

.popup-calendar {
  position: absolute;
  top: 100%;
  z-index: 1430;

  @media (min-width: 640px) {
    margin-top: 0.625rem;
  }

  &--placement-left {
    // left-0 right-auto
    left: 0;
    right: auto;
  }

  &--placement-right {
    // left-auto right-0
    left: auto;
    right: 0;
  }


  &__transition {
    &--enter-from-class {
      --transform-translate-y: 0.75rem;
      opacity: 0;
    }

    &--enter-to-class {
      --transform-translate-y: 0;
      opacity: 1;
    }

    &--enter-active-class {
      transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 300ms;
      transition-duration: 200ms;
      transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
    }

    &--leave-active-class {
      transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 300ms;
      transition-duration: 150ms;
      transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
    }

    &--leave-from-class {
      --transform-translate-y: 0;
      opacity: 1;
    }

    &--leave-to-class {
      --transform-translate-y: 0.75rem;
      opacity: 0;
    }
  }

  &__wrapper {
    overflow-y: auto;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 50;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    @media (min-width: 640px) {
      overflow: visible;
      position: static;
      z-index: auto;
      border-radius: 0.5rem;
    }
  }

  &__box {
    position: static;
    width: 100%;
    padding: 0.75rem;
    background-color: #182737;
    border: 1px solid #344859;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

    @media (min-width: 640px) {
      position: relative;
      padding: 0.375rem 0.25rem;
      border-radius: 0.5rem;
      border-width: 1px;
    }
  }

  &__main {
    display: flex;
    flex-wrap: wrap;

    @media (min-width: 1024px) {
      flex-wrap: nowrap;
    }
  }

  &__calendar {
    display: flex;
    position: relative;
    padding: 0.25rem;
    flex-wrap: wrap;

    @media (min-width: 640px) {
      flex-wrap: nowrap;
    }

    &__header {
      padding-left: 0.125rem;
      padding-right: 0.125rem;

      @media (min-width: 640px) {
        padding-left: 0.5rem;
        padding-right: 0.5rem;
      }

    }

    &__left-wrap {
      position: relative;
      width: 100%;

      @media (min-width: 640px) {
        width: 28.8rem;
      }

      &--range {
        margin-bottom: 0.75rem;

        @media (min-width: 640px) {
          margin-bottom: 0;
          margin-right: 0.5rem;
        }
      }
    }

    &__right-wrap {
      overflow: hidden;
      position: relative;
      margin-top: 0.75rem;
      width: 100%;

      @media (min-width: 640px) {
        margin-top: 0;
        margin-left: 0.5rem;
        width: 28.8rem;
      }
    }
  }
}
</style>
